import React, { useState, useEffect, useRef } from 'react';
import { Settings, MapPin, Upload, Save, X, Clock, Camera, AlertCircle, Calendar, MonitorSmartphone, Plus, Trash2, Power } from 'lucide-react';
import { api, API_BASE } from '../utils/api';
import { getCurrentLocation } from '../utils/helpers';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

const MySwal = withReactContent(Swal);

// คอมโพเนนต์ย่อยสำหรับอัปโหลดรูป
const ImageUploader = ({ title, description, preview, value, onUpload, onRemove, inputRef }) => {
  
  // ฟังก์ชัน helper ตรวจสอบและสร้าง URL ที่ถูกต้องสำหรับแท็ก <img>
  const getImageUrl = (src) => {
    if (!src) {
      return ''; 
    }
    // ถ้า src เป็น Base64 (ที่เพิ่งอัปโหลดใหม่) ให้แสดงผลเลย
    if (src.startsWith('data:image')) {
      return src;
    }
    // ถ้า src เป็น Path (จากฐานข้อมูล) ให้เติม API_BASE ข้างหน้า
    return `${API_BASE}${src}`;
  };

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="px-6 py-4 bg-gray-50 border-b">
        <h2 className="text-xl font-semibold text-gray-900 flex items-center">
          <Upload className="h-6 w-6 mr-2 text-blue-500" />
          {title}
        </h2>
      </div>
      <div className="p-6">
        <div className="flex flex-col items-center space-y-4">
          {preview ? (
            <div className="relative inline-block">
              <img 
                src={getImageUrl(preview)} 
                alt={title} 
                className="h-40 w-40 object-contain mx-auto border-2 border-dashed border-gray-300 rounded-lg p-4 bg-gray-50"
              />
              <button
                onClick={onRemove}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 transition-colors shadow-lg"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div className="h-40 w-40 mx-auto border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
              <div className="text-center">
                <Upload className="h-10 w-10 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">{description}</p>
              </div>
            </div>
          )}
          
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            onChange={onUpload}
            className="hidden"
          />
          
          <div className="flex flex-col items-center space-y-2">
            <button
              onClick={() => inputRef.current?.click()}
              className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-sm transition-colors flex items-center space-x-2"
            >
              <Upload className="h-4 w-4" />
              <span>{preview ? 'เปลี่ยนรูป' : 'เลือกรูป'}</span>
            </button>
            <p className="text-xs text-gray-500 text-center">รองรับไฟล์ JPG, PNG (ขนาดไม่เกิน 2MB)</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const OrganizationSettings = () => {
  const [organization, setOrganization] = useState({
    id: '',
    name: '',
    latitude: '',
    longitude: '',
    address: '',
    logo: '',
    garuda_emblem: '',
    work_group_name: '',
    affiliation_name: '',
    work_start_time: '08:30',
    work_end_time: '17:30',
    max_distance: 1.0,
    allow_wfh: false,
    max_leave_days_per_year: 22,
    max_leave_times_per_year: 7,
    report_director_name: '',
    report_director_position: 'ผู้อำนวยการโรงเรียน',
    report_vp_name: '',
    report_vp_position: 'รองผู้อำนวยการกลุ่มบริหารงานบุคคล',
    report_hr_head_name: '',
    report_hr_head_position: 'หัวหน้ากลุ่มบริหารงานบุคคล',
    report_stat_head_name: '',
    report_stat_head_position: 'หัวหน้างานสถิติ',
    report_discipline_head_name: '',
    report_discipline_head_position: 'หัวหน้างานวินัยและการรักษาวินัย',
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [logoPreview, setLogoPreview] = useState('');
  const [garudaPreview, setGarudaPreview] = useState('');
  
  const logoInputRef = useRef(null);
  const garudaInputRef = useRef(null);

  const [locationLoading, setLocationLoading] = useState(false);

  // State สำหรับจัดการเครื่องสแกน ZKTeco
  const [devices, setDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(false);

  useEffect(() => {
    loadOrganization();
    loadDevices();
  }, []);

  const loadOrganization = async () => {
    try {
      const data = await api.getOrganizations();
      if (data.length > 0) {
        const org = data[0];
        setOrganization(prevState => ({
          ...prevState,
          ...org,
          work_group_name: org.work_group_name || '',
          affiliation_name: org.affiliation_name || '',
          work_start_time: org.work_start_time ? org.work_start_time.substring(0, 5) : '08:30',
          work_end_time: org.work_end_time ? org.work_end_time.substring(0, 5) : '17:30',
          max_leave_days_per_year: org.max_leave_days_per_year !== undefined ? org.max_leave_days_per_year : 22,
          max_leave_times_per_year: org.max_leave_times_per_year !== undefined ? org.max_leave_times_per_year : 7,
          allow_wfh: org.allow_wfh == 1,
          report_director_position: org.report_director_position || 'ผู้อำนวยการโรงเรียน',
          report_vp_position: org.report_vp_position || 'รองผู้อำนวยการกลุ่มบริหารงานบุคคล',
          report_hr_head_position: org.report_hr_head_position || 'หัวหน้ากลุ่มบริหารงานบุคคล',
          report_stat_head_position: org.report_stat_head_position || 'หัวหน้างานสถิติ',
          report_discipline_head_name: org.report_discipline_head_name || '',
          report_discipline_head_position: org.report_discipline_head_position || 'หัวหน้างานวินัยและการรักษาวินัย',
        }));
        
        if (org.logo) {
          setLogoPreview(org.logo);
        }
        if (org.garuda_emblem) {
          setGarudaPreview(org.garuda_emblem);
        }
      }
    } catch (error) {
      console.error('Error loading organization:', error);
      MySwal.fire({
        icon: 'error',
        title: 'โหลดข้อมูลไม่สำเร็จ',
        text: 'ไม่สามารถโหลดข้อมูลหน่วยงานได้',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (event, type) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        MySwal.fire({
          icon: 'warning',
          title: 'ขนาดไฟล์เกินกำหนด',
          text: 'ไฟล์รูปภาพต้องมีขนาดไม่เกิน 2MB',
        });
        return;
      }
      if (!file.type.startsWith('image/')) {
        MySwal.fire({
          icon: 'warning',
          title: 'รูปแบบไฟล์ไม่ถูกต้อง',
          text: 'กรุณาเลือกไฟล์รูปภาพเท่านั้น',
        });
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target.result;
        if (type === 'logo') {
          setOrganization({ ...organization, logo: base64 });
          setLogoPreview(base64);
        } else if (type === 'garuda') {
          setOrganization({ ...organization, garuda_emblem: base64 });
          setGarudaPreview(base64);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = (type) => {
    if (type === 'logo') {
      setOrganization({ ...organization, logo: '' });
      setLogoPreview('');
      if (logoInputRef.current) logoInputRef.current.value = '';
    } else if (type === 'garuda') {
      setOrganization({ ...organization, garuda_emblem: '' });
      setGarudaPreview('');
      if (garudaInputRef.current) garudaInputRef.current.value = '';
    }
  };

  const handleGetCurrentLocation = async () => {
    setLocationLoading(true);
    try {
      const location = await getCurrentLocation();
      setOrganization({
        ...organization,
        latitude: location.latitude.toString(),
        longitude: location.longitude.toString()
      });
      MySwal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: 'ได้รับตำแหน่งปัจจุบันเรียบร้อยแล้ว',
        showConfirmButton: false,
        timer: 3000
      });
    } catch (error) {
      console.error('Error getting location:', error);
      MySwal.fire({
        icon: 'error',
        title: 'ไม่สามารถรับตำแหน่งได้',
        text: 'กรุณาตรวจสอบการอนุญาตเข้าถึงตำแหน่ง (Location)',
      });
    } finally {
      setLocationLoading(false);
    }
  };

  const handleSave = async () => {
    if (!organization.name.trim()) {
      MySwal.fire({
        icon: 'warning',
        title: 'ข้อมูลไม่ครบถ้วน',
        text: 'กรุณาใส่ชื่อหน่วยงาน',
      });
      return;
    }

    const result = await MySwal.fire({
      title: 'ยืนยันการบันทึก?',
      text: "คุณต้องการบันทึกการเปลี่ยนแปลงการตั้งค่าหรือไม่",
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#22c55e',
      cancelButtonColor: '#ef4444',
      confirmButtonText: 'ใช่, บันทึกเลย',
      cancelButtonText: 'ยกเลิก'
    });

    if (result.isConfirmed) {
      setSaving(true);
      try {
        const data = await api.updateOrganization(organization);
        if (data.success) {
          await MySwal.fire({
            icon: 'success',
            title: 'บันทึกสำเร็จ!',
            text: 'ข้อมูลถูกปรับปรุงเรียบร้อยแล้ว',
            timer: 2000,
            showConfirmButton: false
          });
          loadOrganization();
        } else {
          MySwal.fire({
            icon: 'error',
            title: 'ไม่สามารถบันทึกข้อมูลได้',
            text: data.error,
          });
        }
      } catch (error) {
        console.error('Error saving organization:', error);
        MySwal.fire({
          icon: 'error',
          title: 'เกิดข้อผิดพลาด',
          text: error.message,
        });
      } finally {
        setSaving(false);
      }
    }
  };

  // ==========================================
  // ฟังก์ชันสำหรับจัดการเครื่องสแกน ZKTeco (อัปเดตใหม่)
  // ==========================================
  const loadDevices = async () => {
    try {
      setLoadingDevices(true);
      // เปลี่ยนมาใช้ api ส่วนกลาง
      const data = await api.getZktecoDevices();
      
      if (Array.isArray(data)) {
        setDevices(data);
      } else {
        setDevices([]);
      }
    } catch (error) {
      console.error('Error loading devices:', error);
      setDevices([]);
    } finally {
      setLoadingDevices(false);
    }
  };

  const handleAddDevice = async () => {
    const { value: formValues } = await MySwal.fire({
      title: 'เพิ่มเครื่องสแกน ZKTeco',
      html: `
        <div class="space-y-4 text-left">
          <div>
            <label class="block text-sm font-medium text-gray-700">Serial Number (SN) *</label>
            <input id="swal-sn" class="swal2-input !w-full !m-0 !mt-1" placeholder="เช่น CBEZ212345678">
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700">ชื่อเรียกเครื่องสแกน *</label>
            <input id="swal-name" class="swal2-input !w-full !m-0 !mt-1" placeholder="เช่น หน้าเสาธง">
          </div>
        </div>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'บันทึก',
      cancelButtonText: 'ยกเลิก',
      preConfirm: () => {
        return {
          serial_number: document.getElementById('swal-sn').value,
          device_name: document.getElementById('swal-name').value
        }
      }
    });

    if (formValues) {
      if (!formValues.serial_number || !formValues.device_name) {
        return MySwal.fire('แจ้งเตือน', 'กรุณากรอกข้อมูลให้ครบถ้วน', 'warning');
      }
      try {
        // ใช้ api ส่วนกลางในการเพิ่มข้อมูล
        const res = await api.addZktecoDevice(formValues);
        if (res.success) {
          MySwal.fire({ icon: 'success', title: 'เพิ่มเรียบร้อย', showConfirmButton: false, timer: 1500 });
          loadDevices();
        }
      } catch (error) {
        MySwal.fire('ข้อผิดพลาด', error.message, 'error');
      }
    }
  };

  const handleToggleDeviceStatus = async (id, currentStatus) => {
    try {
      await api.updateZktecoDeviceStatus(id, !currentStatus);
      loadDevices();
    } catch (error) {
      console.error(error);
      MySwal.fire('ข้อผิดพลาด', error.message, 'error');
    }
  };

  const handleDeleteDevice = async (id) => {
    const result = await MySwal.fire({
      title: 'ลบเครื่องสแกน?',
      text: "คุณต้องการลบเครื่องนี้ออกจากระบบใช่หรือไม่?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#d1d5db',
      confirmButtonText: 'ลบเลย',
      cancelButtonText: 'ยกเลิก'
    });
    
    if (result.isConfirmed) {
      try {
        await api.deleteZktecoDevice(id);
        loadDevices();
      } catch (error) {
        console.error(error);
        MySwal.fire('ข้อผิดพลาด', error.message, 'error');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">
          ตั้งค่าระบบ (Organization)
        </h1>
        <p className="text-gray-600 mt-2">
          จัดการข้อมูลหลักขององค์กร เวลาทำงาน ตำแหน่งที่ตั้ง โควต้าการลา และอุปกรณ์
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* --- คอลัมน์ 1 --- */}
        <div className="space-y-6">
          
          {/* Card: ข้อมูลพื้นฐาน */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
             <div className="px-6 py-4 bg-gray-50 border-b">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <Settings className="h-6 w-6 mr-2 text-blue-500" />
                ข้อมูลพื้นฐาน
              </h2>
            </div>
            <div className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">กลุ่มงาน</label>
                <input
                  type="text"
                  value={organization.work_group_name}
                  onChange={(e) => setOrganization({...organization, work_group_name: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="เช่น กลุ่มบริหารงานบุคคล"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">ชื่อหน่วยงาน *</label>
                <input
                  type="text"
                  value={organization.name}
                  onChange={(e) => setOrganization({...organization, name: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="เช่น โรงเรียน..."
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">สังกัด</label>
                <input
                  type="text"
                  value={organization.affiliation_name}
                  onChange={(e) => setOrganization({...organization, affiliation_name: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="เช่น สพม..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">ที่อยู่</label>
                <textarea
                  value={organization.address}
                  onChange={(e) => setOrganization({...organization, address: e.target.value})}
                  rows="3"
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="ที่อยู่ของหน่วยงาน"
                />
              </div>
            </div>
          </div>

          {/* Card: เวลาทำงาน */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <Clock className="h-6 w-6 mr-2 text-blue-500" />
                เวลาทำงาน
              </h2>
            </div>
            <div className="p-6">
              <p className="text-gray-600 text-sm mb-4">
                กำหนดเวลาทำงานมาตรฐาน ใช้สำหรับคำนวณสถานะการมาสาย
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">เวลาเริ่มงาน</label>
                  <input
                    type="time"
                    value={organization.work_start_time}
                    onChange={(e) => setOrganization({...organization, work_start_time: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">เวลาเลิกงาน</label>
                  <input
                    type="time"
                    value={organization.work_end_time}
                    onChange={(e) => setOrganization({...organization, work_end_time: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Card: โควต้าการลา */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <Calendar className="h-6 w-6 mr-2 text-green-500" />
                โควต้าการลา (ลาป่วย/ลากิจ)
              </h2>
            </div>
            <div className="p-6">
              <p className="text-gray-600 text-sm mb-4">
                กำหนดจำนวนวันและจำนวนครั้งสูงสุดต่อปี (ใส่ 0 สำหรับไม่จำกัด)
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">รวมจำนวนครั้ง (สูงสุด)</label>
                  <input
                    type="number" min="0"
                    value={organization.max_leave_times_per_year}
                    onChange={(e) => setOrganization({...organization, max_leave_times_per_year: parseInt(e.target.value) || 0})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">รวมจำนวนวันลา (สูงสุด)</label>
                  <input
                    type="number" min="0"
                    value={organization.max_leave_days_per_year}
                    onChange={(e) => setOrganization({...organization, max_leave_days_per_year: parseInt(e.target.value) || 0})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Card: ผู้ลงนามในรายงาน */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <Calendar className="h-6 w-6 mr-2 text-teal-500" />
                ผู้บริหาร/เจ้าหน้าที่
              </h2>
            </div>
            <div className="p-6 space-y-6">
              
              {/* 1. ผู้อำนวยการ */}
              <div>
                <h3 className="text-md font-semibold text-gray-800 mb-3">1. ผู้อำนวยการ</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">ชื่อ-สกุล</label>
                    <input
                      type="text"
                      value={organization.report_director_name}
                      onChange={(e) => setOrganization({...organization, report_director_name: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                      placeholder="เช่น นาย ก..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">ตำแหน่ง</label>
                    <input
                      type="text"
                      value={organization.report_director_position}
                      onChange={(e) => setOrganization({...organization, report_director_position: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                </div>
              </div>

              {/* 2. รองผู้อำนวยการ */}
              <div>
                <h3 className="text-md font-semibold text-gray-800 mb-3">2. รองผู้อำนวยการ</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">ชื่อ-สกุล</label>
                    <input
                      type="text"
                      value={organization.report_vp_name}
                      onChange={(e) => setOrganization({...organization, report_vp_name: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                      placeholder="เช่น นาง ข..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">ตำแหน่ง</label>
                    <input
                      type="text"
                      value={organization.report_vp_position}
                      onChange={(e) => setOrganization({...organization, report_vp_position: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                </div>
              </div>

              {/* 3. หัวหน้ากลุ่มบริหาร */}
              <div>
                <h3 className="text-md font-semibold text-gray-800 mb-3">3. หัวหน้าสำนักงาน</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">ชื่อ-สกุล</label>
                    <input
                      type="text"
                      value={organization.report_hr_head_name}
                      onChange={(e) => setOrganization({...organization, report_hr_head_name: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                      placeholder="เช่น นาย ค..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">ตำแหน่ง</label>
                    <input
                      type="text"
                      value={organization.report_hr_head_position}
                      onChange={(e) => setOrganization({...organization, report_hr_head_position: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                </div>
              </div>

              {/* 4. หัวหน้างานสถิติ */}
              <div>
                <h3 className="text-md font-semibold text-gray-800 mb-3">4. หัวหน้างานสถิติบุคลากร</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">ชื่อ-สกุล</label>
                    <input
                      type="text"
                      value={organization.report_stat_head_name}
                      onChange={(e) => setOrganization({...organization, report_stat_head_name: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                      placeholder="เช่น นาง ง..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">ตำแหน่ง</label>
                    <input
                      type="text"
                      value={organization.report_stat_head_position}
                      onChange={(e) => setOrganization({...organization, report_stat_head_position: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                </div>
              </div>

              {/* 5. หัวหน้างานวินัย */}
              <div>
                <h3 className="text-md font-semibold text-gray-800 mb-3">5. หัวหน้างานวินัยและการรักษาวินัย</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">ชื่อ-สกุล</label>
                    <input
                      type="text"
                      value={organization.report_discipline_head_name}
                      onChange={(e) => setOrganization({...organization, report_discipline_head_name: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                      placeholder="เช่น นางสาว..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">ตำแหน่ง</label>
                    <input
                      type="text"
                      value={organization.report_discipline_head_position}
                      onChange={(e) => setOrganization({...organization, report_discipline_head_position: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* --- คอลัมน์ 2 --- */}
        <div className="space-y-6">

          {/* Card: โลโก้หน่วยงาน */}
          <ImageUploader
            title="โลโก้หน่วยงาน"
            description="อัปโหลดโลโก้"
            preview={logoPreview}
            onUpload={(e) => handleImageUpload(e, 'logo')}
            onRemove={() => removeImage('logo')}
            inputRef={logoInputRef}
          />
          
          {/* Card: ตราครุฑ */}
          <ImageUploader
            title="ตราครุฑ (สำหรับบันทึกข้อความ)"
            description="อัปโหลดตราครุฑ"
            preview={garudaPreview}
            onUpload={(e) => handleImageUpload(e, 'garuda')}
            onRemove={() => removeImage('garuda')}
            inputRef={garudaInputRef}
          />

          {/* Card: ตำแหน่งที่ตั้ง */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <MapPin className="h-6 w-6 mr-2 text-purple-500" />
                ตำแหน่งที่ตั้งและระยะห่าง
              </h2>
            </div>
            <div className="p-6 space-y-6">
              {/* WFH Mode Toggle */}
              <div className="bg-white rounded-xl p-4 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Camera className="h-5 w-5 text-indigo-600" />
                    <div>
                      <h4 className="font-semibold text-gray-900">โหมด Work From Home (WFH)</h4>
                      <p className="text-sm text-gray-600">อนุญาตให้ลงเวลาโดยไม่ตรวจสอบตำแหน่ง</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={organization.allow_wfh}
                      onChange={(e) => setOrganization({...organization, allow_wfh: e.target.checked})}
                      className="sr-only peer"
                    />
                    <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
                {organization.allow_wfh && (
                  <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-xs text-blue-700 flex items-center">
                      <AlertCircle className="h-3 w-3 mr-1" />
                      เมื่อเปิดใช้งาน พนักงานสามารถลงเวลาได้จากทุกที่
                    </p>
                  </div>
                )}
              </div>
              
              <p className="text-gray-600 text-sm">
                ใช้ตำแหน่งนี้เป็นจุดอ้างอิง (เว้นแต่เปิดโหมด WFH)
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">ละติจูด (Latitude)</label>
                  <input
                    type="number"
                    step="any"
                    value={organization.latitude}
                    onChange={(e) => setOrganization({...organization, latitude: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md bg-gray-100"
                    placeholder="เช่น 13.7563"
                    readOnly
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">ลองจิจูด (Longitude)</label>
                  <input
                    type="number"
                    step="any"
                    value={organization.longitude}
                    onChange={(e) => setOrganization({...organization, longitude: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md bg-gray-100"
                    placeholder="เช่น 100.5018"
                    readOnly
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">ระยะห่างสูงสุด (กิโลเมตร)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max="10"
                  value={organization.max_distance}
                  onChange={(e) => setOrganization({...organization, max_distance: parseFloat(e.target.value) || 1.0})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="1.0"
                />
                <p className="text-xs text-gray-500 mt-2">(แนะนำ 0.01 - 0.50 กิโลเมตร หรือ 10-500 เมตร)</p>
              </div>
              
              <button
                onClick={handleGetCurrentLocation}
                disabled={locationLoading}
                className="w-full bg-indigo-500 text-white py-3 px-4 rounded-md hover:bg-indigo-600 disabled:opacity-50 flex items-center justify-center space-x-2 transition-colors"
              >
                {locationLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>กำลังค้นหาตำแหน่ง...</span>
                  </>
                ) : (
                  <>
                    <MapPin className="h-4 w-4" />
                    <span>ใช้ตำแหน่งปัจจุบัน</span>
                  </>
                )}
              </button>
            </div>
          </div>

        </div>
      </div>

      {/* Card: จัดการเครื่องสแกน ZKTeco */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <MonitorSmartphone className="h-6 w-6 mr-2 text-indigo-500" />
            จัดการเครื่องสแกน ZKTeco (ADMS)
          </h2>
          <button 
            onClick={handleAddDevice}
            className="flex items-center text-sm bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-md transition"
          >
            <Plus className="h-4 w-4 mr-1" /> เพิ่มเครื่องสแกน
          </button>
        </div>
        <div className="p-6">
          {loadingDevices ? (
             <div className="text-center py-4 text-gray-500">กำลังโหลด...</div>
          ) : devices.length === 0 ? (
             <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                ยังไม่มีเครื่องสแกนที่ผูกกับหน่วยงานนี้
             </div>
          ) : (
             <div className="overflow-x-auto">
               <table className="min-w-full divide-y divide-gray-200">
                 <thead className="bg-gray-50">
                   <tr>
                     <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Serial Number</th>
                     <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ชื่อเรียกเครื่อง</th>
                     <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">สถานะ</th>
                     <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">จัดการ</th>
                   </tr>
                 </thead>
                 <tbody className="bg-white divide-y divide-gray-200">
                   {devices.map((device) => (
                     <tr key={device.id}>
                       <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{device.serial_number}</td>
                       <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{device.device_name}</td>
                       <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                          <button 
                            onClick={() => handleToggleDeviceStatus(device.id, device.is_active)}
                            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${device.is_active ? 'bg-green-100 text-green-800 hover:bg-green-200' : 'bg-red-100 text-red-800 hover:bg-red-200'} transition-colors`}
                          >
                            <Power className="h-3 w-3 mr-1" /> {device.is_active ? 'เปิดใช้งาน' : 'ระงับ'}
                          </button>
                       </td>
                       <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                         <button 
                           onClick={() => handleDeleteDevice(device.id)}
                           className="text-red-500 hover:text-red-700 transition-colors p-1"
                           title="ลบเครื่องสแกน"
                         >
                           <Trash2 className="h-5 w-5" />
                         </button>
                       </td>
                     </tr>
                   ))}
                 </tbody>
               </table>
             </div>
          )}
        </div>
      </div>

      {/* Card: ปุ่มบันทึก */}
      <div className="bg-white rounded-lg shadow p-6 space-y-6">
        <div>
          <button
            onClick={handleSave}
            disabled={saving || !organization.name.trim()}
            className="w-full bg-green-500 text-white py-4 px-6 rounded-md hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 text-lg font-medium transition-colors"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span>กำลังบันทึก...</span>
              </>
            ) : (
              <>
                <Save className="h-5 w-5" />
                <span>บันทึกการตั้งค่า</span>
              </>
            )}
          </button>
        </div>
      </div>

    </div>
  );
};

export default OrganizationSettings;